import { useState } from 'react';
import { Tab } from '@headlessui/react';
import {
  UserGroupIcon,
  ArrowPathRoundedSquareIcon,
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import ResponseFlow from '../components/ResponseFlow';
import ActivityStep from '../components/ResponsePlan/ActivityStep';
import ChecklistSection from '../components/ResponsePlan/ChecklistSection';
import ActiveResponseProcedures from '../components/CrisisResponse/ActiveResponseProcedures';

const activitySteps = [
  {
    number: '1',
    title: 'Initial Detection',
    responsible: 'CSIRT',
    description: 'Monitor security operations and IT systems for suspicious activities through SOC, alerts, and organizational reports',
    status: 'not-started' as const,
    lastUpdate: {
      role: 'CSIRT Lead',
      timestamp: '2024-01-15 14:30',
    },
  },
  {
    number: '2',
    title: 'Incident Assessment',
    responsible: 'CSIRT',
    description: 'Evaluate alerts, investigate nature/impact/severity, classify incidents',
    status: 'not-started' as const,
    lastUpdate: {
      role: 'CSIRT Analyst',
      timestamp: '2024-01-15 14:35',
    },
  },
  {
    number: '3.1',
    title: 'Standard Response',
    responsible: 'CSIRT',
    description: 'Handle low/medium severity incidents per SOP',
    status: 'not-started' as const,
    isSubStep: true,
  },
  {
    number: '3.2',
    title: 'High Severity Response',
    responsible: 'CSIRT Lead',
    description: 'Assign LIH, complete CCIF, submit to EIC',
    status: 'not-started' as const,
    isSubStep: true,
  },
  {
    number: '3.3',
    title: 'CCMP Activation Assessment',
    responsible: 'EIC',
    description: 'Review CCIF, consult with legal/key parties, determine CCMP activation',
    status: 'not-started' as const,
    isSubStep: true,
  },
  {
    number: '3.4',
    title: 'Crisis Notification',
    responsible: 'EIC & LIH',
    description: 'Notify CCRT of activation, provide incident details',
    status: 'not-started' as const,
    isSubStep: true,
  },
  {
    number: '3.5',
    title: 'Initial Response Meeting',
    responsible: 'CCRT & LIH',
    description: 'Conduct initial meeting, establish communication channels',
    status: 'not-started' as const,
    isSubStep: true,
  },
  {
    number: '3.6',
    title: 'Strategy Development',
    responsible: 'EIC',
    description: 'Develop containment strategy with CSIRT and CCRT',
    status: 'not-started' as const,
    isSubStep: true,
  },
  {
    number: '3.7',
    title: 'External Support Assessment',
    responsible: 'EIC',
    description: 'Evaluate need for third-party forensics support',
    status: 'not-started' as const,
    isSubStep: true,
  },
  {
    number: '4.1',
    title: 'Execute Containment Strategy',
    responsible: 'CSIRT',
    description: 'Execute containment strategy, risk reduction, and forensic/legal requirements',
    status: 'not-started' as const,
  },
  {
    number: '4.2',
    title: 'Lead CCRT Meetings',
    responsible: 'LIH',
    description: 'Lead subsequent CCRT meetings',
    status: 'not-started' as const,
  },
  {
    number: '4.2.1',
    title: 'Refine Meeting Scope',
    responsible: 'EIC/LIH',
    description: 'Collaboratively refine CCRT meeting scope',
    status: 'not-started' as const,
    isSubStep: true,
  },
  {
    number: '4.3',
    title: 'Law Enforcement Engagement',
    responsible: 'EIC',
    description: 'Determine law enforcement engagement needs in consultation with CSIRT, legal counsel, and stakeholders',
    status: 'not-started' as const,
  },
  {
    number: '4.4',
    title: 'Technical Resource Assessment',
    responsible: 'EIC',
    description: 'Assess need for additional technical resources (with LIH/CSIRT); LIH to lead if required',
    status: 'not-started' as const,
  },
  {
    number: '4.5',
    title: 'Board Communications',
    responsible: 'SEIC',
    description: 'Coordinate with CCET on Board/Operating Committee communications',
    status: 'not-started' as const,
  },
  {
    number: '4.6',
    title: 'Functional Response Plans',
    responsible: 'IRLs',
    description: 'Execute functional incident response plans as appropriate',
    status: 'not-started' as const,
  },
  {
    number: '5.1',
    title: 'Support Normal Operations',
    responsible: 'CSIRT',
    description: 'Support return to normal operations, forensics, and legal requirements',
    status: 'not-started' as const,
  },
  {
    number: '5.2',
    title: 'Lessons Learned Review',
    responsible: 'LIH',
    description: 'Conduct Lessons Learned reviews during BAU transition',
    status: 'not-started' as const,
  },
  {
    number: '5.3',
    title: 'Crisis Response Deactivation',
    responsible: 'LIH',
    description: 'Deactivate crisis response after BAU restoration',
    status: 'not-started' as const,
  },
  {
    number: '5.4',
    title: 'After-Action Report',
    responsible: 'LIH',
    description: 'Create After-Action Report for EIC/SEIC review',
    status: 'not-started' as const,
  },
  {
    number: '5.4.1',
    title: 'After-Action Review',
    responsible: 'EIC',
    description: 'Lead formal After-Action review with CCET, CCNT, CCRT, and CSIRT; file per legal requirements',
    status: 'not-started' as const,
    isSubStep: true,
  },
];

const preConfirmationChecklist = [
  { id: 'pre-1', text: 'Verify incident indicators and collect initial evidence' },
  { id: 'pre-2', text: 'Assess potential impact and scope of the incident' },
  { id: 'pre-3', text: 'Document initial findings and timeline of events' },
  { id: 'pre-4', text: 'Identify affected systems and potential data compromise' },
  { id: 'pre-5', text: 'Evaluate business impact and operational disruption' },
  { id: 'pre-6', text: 'Prepare initial briefing for senior management' },
  { id: 'pre-7', text: 'Review crisis declaration criteria' },
];

const postConfirmationChecklist = [
  { id: 'post-1', text: 'Activate Crisis Management Team and establish command structure' },
  { id: 'post-2', text: 'Set up War Room and communication channels' },
  { id: 'post-3', text: 'Initialize incident tracking and documentation procedures' },
  { id: 'post-4', text: 'Implement immediate containment measures' },
  { id: 'post-5', text: 'Notify key stakeholders according to communication plan' },
  { id: 'post-6', text: 'Engage required external support and resources' },
  { id: 'post-7', text: 'Begin evidence preservation and forensic procedures' },
];

const deactivationChecklist = [
  { id: 'deact-1', text: 'Verify incident containment and threat elimination' },
  { id: 'deact-2', text: 'Confirm restoration of critical business operations' },
  { id: 'deact-3', text: 'Complete all required documentation and reports' },
  { id: 'deact-4', text: 'Conduct preliminary lessons learned assessment' },
  { id: 'deact-5', text: 'Archive incident records and evidence' },
  { id: 'deact-6', text: 'Schedule post-incident review meetings' },
  { id: 'deact-7', text: 'Update crisis response procedures based on experience' },
];

export default function ResponsePlan() {
  const [categories] = useState([
    {
      id: 'workflow',
      name: 'Work Flow',
      icon: ArrowPathRoundedSquareIcon,
      content: <ResponseFlow />,
    },
    {
      id: 'activities',
      name: 'Activities',
      icon: ClipboardDocumentListIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Response Activities</h3>
              <div className="mt-4 space-y-4">
                {activitySteps.map((step) => (
                  <ActivityStep
                    key={step.number}
                    number={step.number}
                    title={step.title}
                    responsible={step.responsible}
                    description={step.description}
                    status={step.status}
                    lastUpdate={step.lastUpdate}
                    isSubStep={step.isSubStep}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'crisis-response',
      name: 'Crisis Response',
      icon: ExclamationTriangleIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <ActiveResponseProcedures />
        </div>
      ),
    },
    {
      id: 'roles',
      name: 'Roles',
      icon: UserGroupIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-6">
              <h3 className="text-lg font-medium">Cyber Crisis Executive Team (CCET)</h3>
              <p className="text-gray-600 mt-1">Strategic oversight and board communication</p>
              <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                <li>Operating Committee members</li>
                <li>Senior Executive-in-Charge (SEIC)</li>
                <li>Ensures adequate information flow to Board of Directors</li>
                <li>Strategic decision-making and oversight</li>
              </ul>
            </div>

            <div className="border-b pb-6">
              <h3 className="text-lg font-medium">Cyber Crisis Management Team (CCMT)</h3>
              <p className="text-gray-600 mt-1">Tactical leadership and crisis management</p>
              <div className="mt-4 space-y-4">
                <div>
                  <h4 className="font-medium">Senior Executive-in-Charge (SEIC)</h4>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>Provides senior management guidance to EIC</li>
                    <li>Serves as escalation point for key decisions</li>
                    <li>Acts as liaison with CCET</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium">Executive-in-Charge (EIC)</h4>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>Full authority for tactical decision making</li>
                    <li>Oversees command-and-control activities</li>
                    <li>Directs crisis response operations</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-b pb-6">
              <h3 className="text-lg font-medium">Cyber Crisis Response Team (CCRT)</h3>
              <p className="text-gray-600 mt-1">Tactical response and incident handling</p>
              <div className="mt-4 space-y-4">
                <div>
                  <p className="text-sm text-gray-600 italic">Led by the Executive-in-Charge (EIC)</p>
                </div>
                <div>
                  <h4 className="font-medium">Lead Incident Handler (LIH)</h4>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>Responsible for incident's tactical coordination</li>
                    <li>Ensures incident response progression</li>
                    <li>Manages timely and accurate stakeholder communication</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium">Functional Group Incident Response Leads (IRL)</h4>
                  <p className="text-sm text-gray-600 mt-1">Responsibilities vary by functional group:</p>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>IT Security IRL: Security measures and technical response</li>
                    <li>Operations IRL: Business continuity and operational impact</li>
                    <li>Legal IRL: Regulatory compliance and legal implications</li>
                    <li>Communications IRL: Internal and external communications</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="border-b pb-6">
              <h3 className="text-lg font-medium">Cyber Crisis Support Team (CCST)</h3>
              <p className="text-gray-600 mt-1">Functional support for crisis management</p>
              <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                <li>Provides departmental expertise and resources</li>
                <li>Supports CCMT with specialized knowledge</li>
                <li>Facilitates cross-functional coordination</li>
                <li>Implements tactical response actions</li>
              </ul>
            </div>

            <div className="border-b pb-6">
              <h3 className="text-lg font-medium">Computer Security Incident Response Team (CSIRT)</h3>
              <p className="text-gray-600 mt-1">Technical response execution</p>
              <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                <li>Coordinates technology-focused response activities</li>
                <li>Executes technical aspects of response plan</li>
                <li>Provides technical expertise and guidance</li>
                <li>Manages security tools and systems</li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'war-room',
      name: 'War Room',
      icon: BuildingOfficeIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">War Room Operations</h3>
              <div className="mt-4 space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Physical Facilities</h4>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>Primary command center setup</li>
                    <li>Backup location readiness</li>
                    <li>Equipment and resources</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Virtual Operations</h4>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>Remote access systems</li>
                    <li>Communication platforms</li>
                    <li>Collaboration tools</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Resource Management</h4>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>Team scheduling</li>
                    <li>Equipment allocation</li>
                    <li>Supply coordination</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'communication',
      name: 'Communication',
      icon: ChatBubbleLeftRightIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Communication Protocols</h3>
              <div className="mt-4 space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Internal Communications</h4>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>Team updates and briefings</li>
                    <li>Status reporting procedures</li>
                    <li>Escalation protocols</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">External Communications</h4>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>Media relations guidelines</li>
                    <li>Stakeholder notifications</li>
                    <li>Public relations management</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Documentation</h4>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>Communication logs</li>
                    <li>Decision records</li>
                    <li>Action tracking</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'documentation',
      name: 'Documentation',
      icon: DocumentTextIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Response Documentation</h3>
              <div className="mt-4 space-y-4">
                <ChecklistSection
                  title="Pre-Confirmation Checklist"
                  description="Critical steps before declaring a cyber crisis"
                  items={preConfirmationChecklist}
                />
                <ChecklistSection
                  title="Post-Confirmation Checklist"
                  description="Immediate actions after crisis declaration"
                  items={postConfirmationChecklist}
                />
                <ChecklistSection
                  title="Deactivation Checklist"
                  description="Steps for crisis response deactivation"
                  items={deactivationChecklist}
                />
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Response Plan</h2>
      <Tab.Group>
        <Tab.List className="flex space-x-4 border-b border-gray-200">
          {categories.map((category) => (
            <Tab
              key={category.id}
              className={({ selected }) =>
                clsx(
                  'flex items-center space-x-2 py-2 px-4 text-sm font-medium border-b-2 outline-none',
                  selected
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )
              }
            >
              <category.icon className="h-5 w-5" />
              <span>{category.name}</span>
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          {categories.map((category) => (
            <Tab.Panel key={category.id}>{category.content}</Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}